const { useRef, useCallback, useState, useMemo } = require('react');

const { TextField } = require('@andes/textfield');
const { Button } = require('@andes/button');
const { Typography } = require('@andes/typography');
const { useI18n } = require('nordic/i18n');
const useMask = require('@andes/textfield/build/hooks/useMask');
const GPSPinOutlined16 = require('@andes/icons/GPSPinOutlined16');
const { ZIPCODE: ZIPCODE_MLB } = require('@andes/textfield/build/masks/br');
const { ZIPCODE: ZIPCODE_MLM } = require('@andes/textfield/build/masks/mx');

const ApiService = require('../../../service/api');
const translate = require('../../../translation');
const { useShippingData } = require('./useShippingData');
const { ModalZipcode } = require('./ModalZipcode');

const ZIPCODE_MLA = {
  mask: 'DDDD',
  tokens: { D: { pattern: /\d/ } },
};

const ZIPCODE_MASK_MAP = {
  MLA: ZIPCODE_MLA,
  MLB: ZIPCODE_MLB,
  MLM: ZIPCODE_MLM,
};

const MIN_CITIES_LEN = 1;

const isZipCodeValidLen = ({ zipcode, selectedMaskOptions }) =>
  selectedMaskOptions?.tokens?.D.pattern.test(zipcode) &&
  selectedMaskOptions?.mask?.length === zipcode?.length;

const ZipcodeInput = ({ onCalculate }) => {
  const { i18n } = useI18n();
  const translations = translate(i18n);
  const zipcodeInputRef = useRef();
  const zipcodeModalRef = useRef();
  const {
    data: {
      siteId,
      zipcode: defaultZipcode = '',
      locationPayload = {},
      hasZipcodeFinder,
      zipcodeError,
    },
    setShippinData,
  } = useShippingData();

  const [zipcode, setZipcode] = useState(defaultZipcode);
  const hasAddress = locationPayload?.cities?.length;
  const { cities, state, selected_city: selectedCity } = locationPayload;
  const selectedMaskOptions = ZIPCODE_MASK_MAP[siteId];

  useMask(zipcodeInputRef, { maskOptions: selectedMaskOptions });

  const getLocation = useCallback(
    async ({ zipcode }) => {
      try {
        if (isZipCodeValidLen({ zipcode, selectedMaskOptions })) {
          const { locations } = await ApiService.getLocations(zipcode);

          if (locations?.length) {
            const currentLocation = locations[0];

            const locationPayloadUpdated = {
              zip_code: String(zipcode.replace(/\D/g, '')),
              state: currentLocation.state.name,
              selected_city: currentLocation.city.name,
              cities: locations.map(({ city }) => city.name),
            };

            return locationPayloadUpdated;
          }
        }
      } finally {
        // do nothing
      }

      return Promise.resolve(undefined);
    },
    [selectedMaskOptions],
  );

  const handleCalculate = useCallback(async ({ zipcode }) => {
    const locationPayloadUpdated = await getLocation({ zipcode });
    setShippinData({
      locationPayload: locationPayloadUpdated,
      zipcode,
      zipcodeError: !locationPayloadUpdated,
    });

    if (locationPayloadUpdated) {
      onCalculate({ locationPayload: locationPayloadUpdated });
    }
  }, []);

  const resetInput = useCallback(() => {
    setShippinData({
      zipcode: '',
      locationPayload: {},
      shippingOptions: [],
      selectedShippingOption: undefined,
      hasShippingOptions: false,
      zipcodeError: false,
    });
  }, []);

  const onChangeZipcodeInput = useCallback(
    ({ zipcode }) => {
      if (
        selectedMaskOptions?.mask?.length === zipcode?.length &&
        zipcode !== defaultZipcode
      ) {
        resetInput();
      }

      setZipcode(zipcode);

      if (!isZipCodeValidLen({ zipcode, selectedMaskOptions })) {
        resetInput();
      }
    },
    [defaultZipcode],
  );

  const doNotHaveZipcode = useMemo(
    () =>
      hasAddress || !hasZipcodeFinder ? undefined : (
        <Typography
          size="xs"
          color="link"
          weight="semibold"
          className="btn-zipcode-finder"
          onClick={() => zipcodeModalRef.current.openModal()}
        >
          {translations.MY_CODE}
        </Typography>
      ),
    [hasAddress, hasZipcodeFinder, translations],
  );

  return (
    <div className="sub-content">
      <div>
        <TextField
          label={translations.LABEL_ZIPCODE}
          srLabel={translations.LABEL_ZIPCODE}
          placeholder={translations.SHIPPING_PLACEHOLDER_ZIPCODE}
          ref={zipcodeInputRef}
          onChange={(e) => onChangeZipcodeInput({ zipcode: e.target.value })}
          value={zipcode}
          className="input-zipcode"
          clear={hasAddress}
          modifier={zipcodeError ? 'error' : undefined}
          helper={
            zipcodeError
              ? translations.SHIPPING_ZIPCODE_TYPED_ERROR
              : doNotHaveZipcode
          }
        >
          {hasAddress ? undefined : (
            <Button
              hierarchy="quiet"
              size="medium"
              onClick={() => {
                handleCalculate({ zipcode });
              }}
              disabled={!isZipCodeValidLen({ zipcode, selectedMaskOptions })}
              className="btn-calculate"
            >
              {translations.CALCULATE_SHIPPING}
            </Button>
          )}
        </TextField>
      </div>

      {hasAddress ? (
        <Typography
          className="shipping-address-selected"
          size="s"
          type="body"
          component="div"
        >
          <GPSPinOutlined16 />

          {cities?.length > MIN_CITIES_LEN
            ? i18n.gettext('CP {0} - {1}.', zipcode, state)
            : i18n.gettext('CP {0} - {1}, {2}.', zipcode, state, selectedCity)}
        </Typography>
      ) : undefined}

      {hasZipcodeFinder ? (
        <ModalZipcode
          ref={zipcodeModalRef}
          onSelectZipcode={onChangeZipcodeInput}
        />
      ) : undefined}
    </div>
  );
};

module.exports = { ZipcodeInput };
